import React, { Suspense, lazy } from 'react'
import { PageProps, graphql } from 'gatsby'
import { ImageDataLike, StaticImage, getImage, getSrc } from 'gatsby-plugin-image'
import { IconBaseProps } from 'react-icons'
import { FaHandHoldingUsd, FaRegLightbulb } from 'react-icons/fa'
import { GiBrazil } from 'react-icons/gi'
import { MdDomain, MdOutlineQuestionAnswer, MdMoney, MdSmartphone } from 'react-icons/md'

import { Button, ContactForm, Container, Loading, Seo, WhatsAppButton } from 'components'
import { Layout } from 'components/layouts'
import AvailablePlans, { PlanCategory } from 'components/Plans'
import { Hero } from 'components/sections'
import { useAnalytics } from 'hooks'
import { classNames } from 'services/string'

const FAQ = lazy(() => import('components/sections/FAQ'))

enum AcceptedIcons {
  domain = 'domain',
  holdingUsd = 'holdingUsd',
  question = 'question',
  brazil = 'brazil',
  money = 'money',
  smartphone = 'smartphone',
}

const commonIconProps: IconBaseProps = {
  size: '2rem',
  color: 'white',
  display: 'inline',
}

const IconMapping: Record<AcceptedIcons, () => React.JSX.Element> = {
  domain: () => <MdDomain {...commonIconProps} />,
  holdingUsd: () => <FaHandHoldingUsd {...commonIconProps} />,
  question: () => <MdOutlineQuestionAnswer {...commonIconProps} />,
  brazil: () => <GiBrazil {...commonIconProps} />,
  money: () => <MdMoney {...commonIconProps} />,
  smartphone: () => <MdSmartphone {...commonIconProps} />,
}

const businessSolutions = [
  {
    icon: <FaHandHoldingUsd {...commonIconProps} />,
    title: 'Fazemos por você',
    description: 'Deixe sua contabilidade na mão de especialistas.',
  },
  {
    icon: <GiBrazil {...commonIconProps} />,
    title: 'Serviço disponível em todo o país',
    description: 'Atendemos você em qualquer lugar do Brasil.',
  },
  {
    icon: <MdOutlineQuestionAnswer {...commonIconProps} />,
    title: 'Tiramos todas as suas dúvidas',
    description: 'Fale com nossos especialistas quando quiser por telefone, e-mail ou WhatsApp.',
  },
  {
    icon: <MdDomain {...commonIconProps} />,
    title: 'Tudo o que sua empresa precisa',
    description: 'Assessoria completa, fechamento contábil, envio da guia de imposto, certidões negativas mensais e descontos exclusivos!',
  },
]

const Page = ({ data }: PageProps<Queries.ProductLandingPageQuery>) => {
  const { fireEvent } = useAnalytics()

  const { lp } = data
  if (!lp) return null
  const heroImage = getImage(lp?.heroSettings?.highlightImage?.localFile as ImageDataLike)

  // Disparo do evento de clique externo
  const fireAnalyticsEvent = (event: 'externalClick' | 'ctaClick', target: string) => {
    fireEvent(event, {
      target,
    })
  }

  const { pricingTitle, pricingDescription, featuredCards, pricingPromotionalOffers, productOffer, ctaContent, contactForm, extraInfo } = lp

  const customPricingList = productOffer?.map((offer) => ({
    ...(offer?.price as Queries.PlansContentFragment),
    name: offer?.name || 'Oferta especial',
    description: offer?.description || 'Aproveite agora',
    metaDescription: offer?.metaDescription || 'oferta',
    category: (offer?.category as PlanCategory) || PlanCategory.Service,
  }))

  const getMappedIcon = (iconSlug?: string | null) =>
    iconSlug && (Object.values(AcceptedIcons) as string[]).includes(iconSlug) ? (
      IconMapping[iconSlug as AcceptedIcons]()
    ) : (
      <FaRegLightbulb {...commonIconProps} />
    )

  return (
    <Layout
      whatsAppContextValue={{
        customMessage: extraInfo?.customWhatsAppMessage || undefined,
        customMessageSlug: lp.slug || undefined,
      }}
    >
      <Suspense fallback={<Loading />}>
        <Hero
          image={heroImage}
          title={lp.heroSettings?.title}
          subtitle={lp.heroSettings?.subtitle}
          description={lp.heroSettings?.description}
          primaryCtaLabel={lp.heroSettings?.primaryCtaLabel}
          showAppStoreBadges={Boolean(lp.heroSettings?.showAppStoreBadges)}
          hideFeatures
        />
      </Suspense>

      <Container className='flex flex-col gap-8 lg:gap-12 py-12 xl:py-20'>
        <div className='flex flex-col gap-4 text-center xl:text-left'>
          <h3 className='font-semibold text-2xl'>Quais são as vantagens?</h3>
          <p>Nossa solução é eficiente e completa, feita para facilitar a sua vida.</p>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8'>
          {featuredCards?.map((card, index) => {
            const { icon, title, description } = card as { icon: string; title: string; description: string }
            const selectedIcon = getMappedIcon(icon)
            return (
              <div
                key={index}
                className={classNames(
                  'rounded-2xl flex flex-col px-4 py-6 h-full gap-4 items-center text-center drop-shadow-2xl',
                  'bg-white/50 shadow-[1px_1px_1px_0px_rgba(255,255,255,1)_inset] text-black transition-all duration-300 opacity-100',
                )}
              >
                <div className='rounded-full shadow-md lg:shadow-xl bg-gradient-to-r from-gray-900 to-gray-600'>
                  <div className='flex justify-center items-center w-16 h-16 text-white'>{selectedIcon}</div>
                </div>
                <div className='flex flex-col gap-2'>
                  <p className='font-medium text-lg'>{title}</p>
                  <p className='text-sm text-black/80'>{description}</p>
                </div>
              </div>
            )
          })}
        </div>
      </Container>

      <div id='planos' style={{ display: 'grid' }}>
        <StaticImage
          className='row-start-1 col-start-1 lg:max-h-[840px] xl:max-h-[550px]'
          layout='fullWidth'
          alt='Imagem de fundo'
          src='../images/grey-bg.png'
        />
        <div style={{ gridArea: '1/1', position: 'relative', placeItems: 'center', display: 'grid' }}>
          <Container className='flex flex-col gap-8 py-6 xl:py-8 text-white'>
            <div className='flex flex-col gap-4 text-center xl:text-left xl:w-2/3'>
              <h3 className='text-primary-600 font-semibold text-2xl'>{pricingTitle}</h3>
              <p>{pricingDescription}</p>
            </div>

            <AvailablePlans
              customPricingOptions={customPricingList}
              secondaryColor='#000000'
              planCardOptions={{
                blurred: true,
              }}
              planBenefitsTitle='O que está incluso:'
              customPlanBenefits={pricingPromotionalOffers?.map((offer) => offer?.item || '')}
            />
          </Container>
        </div>
      </div>

      {contactForm && (
        <section id='contato'>
          <ContactForm
            variant='traditional'
            title={contactForm.title || undefined}
            description={contactForm.description || undefined}
            marketingCampaignCode={lp.campaignCode || undefined}
          />
        </section>
      )}

      <FAQ extraQuestions={lp?.extraFaq as Queries.FAQInfoFragment[]} ignoreGlobalFaq />

      <div className='bg-gradient-to-r from-gray-900 to-gray-600'>
        <Container className='flex flex-col gap-8 py-12 xl:py-16 text-white items-center'>
          <div className='text-center'>
            <h3 className='text-xl font-semibold text-primary-600'>{ctaContent?.title}</h3>
            <p className='mt-4'>{ctaContent?.description}</p>
          </div>
          <WhatsAppButton onClick={() => fireAnalyticsEvent('externalClick', 'linkWhatsapp')}>
            <Button>{ctaContent?.ctaLabel || 'Fale com nossos especialistas'}</Button>
          </WhatsAppButton>
        </Container>
      </div>

      <Container className='flex flex-col gap-8 lg:gap-12 py-12 xl:py-20'>
        <div className='flex flex-col gap-4 text-center lg:text-left lg:w-2/3'>
          <h3 className='font-semibold text-2xl'>Aproveite e conheça as soluções Hubs para sua empresa!</h3>
          <p>Com planos que cabem no seu bolso, sua empresa fica 100% regularizada e passa a contar com diversas soluções, como:</p>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8'>
          {businessSolutions?.map(({ icon, title, description }, index) => {
            return (
              <div
                key={index}
                className={classNames(
                  'rounded-2xl flex flex-col px-4 py-6 h-full gap-4 items-center text-center drop-shadow-2xl',
                  'bg-white/50 shadow-[1px_1px_1px_0px_rgba(255,255,255,1)_inset] text-black transition-all duration-300 opacity-100',
                )}
              >
                <div className='rounded-full shadow-md lg:shadow-xl bg-gradient-to-r from-gray-900 to-gray-600'>
                  <div className='flex justify-center items-center w-16 h-16 text-white'>{icon}</div>
                </div>
                <div className='flex flex-col gap-2'>
                  <p className='font-medium text-lg'>{title}</p>
                  <p className='text-sm text-black/80'>{description}</p>
                </div>
              </div>
            )
          })}
        </div>
        <div className='flex justify-center'>
          <a href='/' rel='noreferrer noopener' target='_BLANK' onClick={() => fireAnalyticsEvent('ctaClick', 'home')}>
            <Button>Quero trazer minha empresa para a Hubs</Button>
          </a>
        </div>
      </Container>
    </Layout>
  )
}

export const Head = ({ data }: PageProps<Queries.ProductLandingPageQuery>) => {
  const { lp } = data
  if (!lp) return null
  const { seo } = lp

  const shareImageUrl = seo?.shareImage?.localFile ? getSrc(seo?.shareImage?.localFile as ImageDataLike) : undefined

  return (
    <Seo
      seo={{
        shareImageUrl,
        metaTitle: seo?.metaTitle || 'Produtos de contabilidade para você | Contabilidade Digital',
        metaDescription: seo?.metaDescription,
      }}
    />
  )
}

export const query = graphql`
  query ProductLandingPage($slug: String!) {
    lp: strapiProductLandingPage(slug: { eq: $slug }) {
      id
      slug
      campaignCode
      heroSettings {
        title
        subtitle
        description
        highlightImage {
          localFile {
            childImageSharp {
              gatsbyImageData(width: 750, height: 550)
            }
          }
        }
        showAppStoreBadges
        primaryCtaLabel
      }
      featuredCards {
        icon
        title
        description
      }
      ctaContent {
        title
        description
        ctaLabel
      }
      pricingTitle
      pricingDescription
      productOffer {
        price {
          ...PlansContent
        }
        name
        description
        metaDescription
        category
      }
      pricingPromotionalOffers {
        item
      }
      contactForm {
        title
        description
        cnpjRequired
      }
      extraFaq {
        ...FAQInfo
      }
      seo {
        metaTitle
        metaDescription
        shareImage {
          localFile {
            url
            childImageSharp {
              gatsbyImageData(layout: FIXED, aspectRatio: 1.777777777, width: 1280, placeholder: NONE)
            }
          }
        }
      }
      extraInfo {
        customWhatsAppMessage
      }
    }
  }
`

export default Page
